import { actions, GuardMeta, sendTo } from "xstate"
import * as O from "fp-ts/Option"
import * as F from "fp-ts/function"
import { RealSuiteNumber, SuiteNumber } from "@ssc-booking/api-contracts"
import * as ROA from "fp-ts/ReadonlyArray"
import { FareCode } from "@silversea-ssc/web-fares-fp"
import { RequestResponseChannel } from "../../../../../_common/machines/data-load-machine"
import { assertEvent } from "../../../../../_common/xstate-helpers"
import { fareCardByKey } from "../../../read-models/fare"
import { validVsCodeValue, vsMember } from "../../../vs-code"
import { paymentTermsUrl } from "../../../payment-terms"
import { buildCruisePaymentTermsLoader, PaymentTermsLoaderTypes } from "../data-loading/payment-terms-loader-machine"
import { buildSuitePricesLoader, SuitePricesLoaderTypes } from "../data-loading/suite-prices-loader-machine"
import { ECommerceEvent, ECommerceStateNodeConfig } from "../e-commerce-machine"
import { AvailableSuitesLoaderTypes, buildAvailableSuitesLoader } from "../data-loading/available-suites-loader-machine"
import { ECommerceContext } from "../e-commerce-machine-context"
import { allFarePricesState } from "./all-fare-prices-state"
import { dataLoadingConfigCommon, dataLoadingOptionsCommon } from "./common"

export const dataLoadingConfigV3 = (websocketClient: RequestResponseChannel): ECommerceStateNodeConfig => ({
    type: "parallel",
    states: {
        ...dataLoadingConfigCommon(websocketClient),
        farePrices: {
            ...allFarePricesState(websocketClient),
        },
        cruisePaymentTerms: {
            invoke: {
                id: "paymentTermsLoader",
                src: buildCruisePaymentTermsLoader(),
            },
            on: {
                FARE_CARDS_LOADED: { actions: ["loadPaymentTerms"] },
                CRUISE_PAYMENT_TERMS_LOADED: { actions: ["setPaymentTerms"] },
            },
        },
        suitePrices: {
            invoke: {
                id: "suitePricesLoader",
                src: buildSuitePricesLoader(websocketClient),
            },
            on: {
                FARE_SELECTED: { cond: "isNotCheckingOut", actions: ["loadSuitePrices"] },
                FARE_CARD_CHANGED: {
                    cond: "isSelectedFare",
                    actions: ["loadSuitePrices"],
                },
                VS_CODE_CHANGED: { cond: "isNotCheckingOut", actions: ["loadSuitePrices"] },
            },
        },
        availableSuites: {
            invoke: {
                id: "availableSuitesLoader",
                src: buildAvailableSuitesLoader(websocketClient),
            },
            on: {
                SUITE_CATEGORY_SELECTED: { cond: "isNotCheckingOut", actions: ["loadAvailableSuites"] },
            },
        },
    },
})

type Context = ECommerceContext
type Events = ECommerceEvent

export const dataLoadingOptionsV3 = {
    guards: {
        ...dataLoadingOptionsCommon.guards,
        isSelectedFare: (ctx: Context, ev: Events, condMeta: GuardMeta<Context, Events>) => {
            assertEvent(ev, "FARE_CARD_CHANGED")
            return (
                ctx.selectedFare?.key == ev.fareCard.key &&
                dataLoadingOptionsCommon.guards.isNotCheckingOut(ctx, ev, condMeta)
            )
        },
    },
    actions: {
        ...dataLoadingOptionsCommon.actions,

        loadPaymentTerms: sendTo<Context, Events, PaymentTermsLoaderTypes["ActorRef"]>(
            "paymentTermsLoader",
            (ctx, ev): PaymentTermsLoaderTypes["Load"] => {
                assertEvent(ev, "FARE_CARDS_LOADED")
                return {
                    type: "LOAD",
                    request: F.pipe(
                        ev.fareCards,
                        ROA.map((x) => x.fare),
                        ROA.uniq(FareCode.eq),
                        ROA.map(
                            (fare) =>
                                new Request(
                                    paymentTermsUrl(
                                        ctx.config,
                                        ctx.geoLocation.countryCode,
                                        ctx.geoLocation.defaultCurrency,
                                        ctx.cruiseCode,
                                        fare,
                                    ),
                                ),
                        ),
                        ROA.toArray,
                    ),
                }
            },
        ),

        loadSuitePrices: actions.pure<Context, Events>((ctx) => {
            const fare = fareCardByKey(ctx, ctx.selectedFare?.key)
            if (!fare) return undefined
            return [
                sendTo<Context, Events, SuitePricesLoaderTypes["ActorRef"]>(
                    "suitePricesLoader",
                    (ctx): SuitePricesLoaderTypes["Load"] => ({
                        type: "LOAD",
                        data: {
                            cruiseCode: ctx.cruiseCode,
                            occupancy: ctx.guests,
                            fareCode: fare.fare,
                            vsMember: vsMember(ctx.vsCode, fare.options.vs),
                            vsCode: O.fromNullable(validVsCodeValue(ctx.vsCode)),
                            availabilities: ctx.flowVersion.availabilities,
                            air: fare.air,
                        },
                        country: ctx.geoLocation.countryCode,
                    }),
                ),
            ]
        }),

        loadAvailableSuites: actions.pure<Context, Events>((ctx) => {
            const fare = fareCardByKey(ctx, ctx.selectedFare?.key)
            const suiteCategory = ctx.selectedSuiteCategory
            if (!fare || !suiteCategory) return undefined

            return [
                sendTo<Context, Events, AvailableSuitesLoaderTypes["ActorRef"]>(
                    "availableSuitesLoader",
                    (ctx): AvailableSuitesLoaderTypes["Load"] => {
                        return {
                            type: "LOAD",
                            data: {
                                cruiseCode: ctx.cruiseCode,
                                occupancy: ctx.guests,
                                fareCode: fare.fare,
                                suiteCategory,
                                preferredSuiteNumber: F.pipe(
                                    O.fromNullable(ctx.preferredSuiteNumber),
                                    O.chain(O.fromPredicate((x): x is RealSuiteNumber => x !== SuiteNumber.guaranteed)),
                                ),
                            },
                            country: ctx.geoLocation.countryCode,
                        }
                    },
                ),
            ]
        }),
    },
}
